import React, { createContext, useContext, useState, ReactNode } from 'react';

export type CurrencyCode = 'XAF' | 'EUR' | 'USD' | 'GBP' | 'CAD';

export interface CurrencyConfig {
  code: CurrencyCode;
  label: string;
  symbol: string;
  flag: string;
  ratePerXaf: number; // e.g. 1 / 655.957 for EUR
  xafRate: number; // e.g. 655.957 for EUR
  isPegged?: boolean;
}

export const CURRENCY_CONFIGS: Record<CurrencyCode, CurrencyConfig> = {
  XAF: {
    code: 'XAF',
    label: 'FCFA (XAF)',
    symbol: 'XAF',
    flag: '🇨🇲',
    ratePerXaf: 1,
    xafRate: 1,
    isPegged: true,
  },
  EUR: {
    code: 'EUR',
    label: 'EUR (€)',
    symbol: '€',
    flag: '🇪🇺',
    ratePerXaf: 1 / 655.957,
    xafRate: 655.957,
    isPegged: true, // Official BEAC / CEMAC fixed peg
  },
  USD: {
    code: 'USD',
    label: 'USD ($)',
    symbol: '$',
    flag: '🇺🇸',
    ratePerXaf: 1 / 600,
    xafRate: 600,
  },
  GBP: {
    code: 'GBP',
    label: 'GBP (£)',
    symbol: '£',
    flag: '🇬🇧',
    ratePerXaf: 1 / 780,
    xafRate: 780,
  },
  CAD: {
    code: 'CAD',
    label: 'CAD ($)',
    symbol: 'CA$',
    flag: '🇨🇦',
    ratePerXaf: 1 / 440,
    xafRate: 440,
  },
};

export interface FormattedPriceResult {
  primary: string;
  secondary?: string;
  currency: CurrencyCode;
  rawXaf: number;
  convertedAmount: number;
}

interface CurrencyContextType {
  currency: CurrencyCode;
  setCurrency: (code: CurrencyCode) => void;
  config: CurrencyConfig;
  parsePriceToXaf: (price?: string | number | null) => number;
  convertFromXaf: (amountXaf: number, targetCurrency?: CurrencyCode) => number;
  formatPrice: (price?: string | number | null, isFr?: boolean) => FormattedPriceResult;
  formatXafWithCode: (price?: string | number | null, isFr?: boolean) => string;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export function parsePriceToXaf(price?: string | number | null): number {
  if (price == null) return 0;
  if (typeof price === 'number') return isNaN(price) ? 0 : price;
  const digits = String(price).replace(/[^\d]/g, '');
  const parsed = parseInt(digits, 10);
  return isNaN(parsed) ? 0 : parsed;
}

export function convertFromXaf(amountXaf: number, targetCurrency: CurrencyCode = 'XAF'): number {
  if (!amountXaf || amountXaf <= 0) return 0;
  const cfg = CURRENCY_CONFIGS[targetCurrency] || CURRENCY_CONFIGS.XAF;
  if (cfg.code === 'XAF') return amountXaf;
  return Math.round(amountXaf / cfg.xafRate);
}

/**
 * Formats a price in XAF ensuring the currency code 'XAF' is always placed
 * strictly AFTER the numerical value (e.g. "35,000,000 XAF").
 */
export function formatXafWithCode(price?: string | number | null, isFr: boolean = false): string {
  const rawXaf = parsePriceToXaf(price);
  if (!rawXaf || rawXaf <= 0) {
    if (typeof price === 'string' && price.trim()) {
      const clean = price.replace(/^(?:XAF|FCFA|CFA|\$|€|£)\s*/i, '').replace(/\s*(?:XAF|FCFA|CFA)$/i, '').trim();
      return clean ? `${clean} XAF` : '0 XAF';
    }
    return '0 XAF';
  }
  return `${rawXaf.toLocaleString(isFr ? 'fr-FR' : 'en-US')} XAF`;
}

export function formatPriceResult(
  price: string | number | null | undefined,
  targetCurrency: CurrencyCode = 'XAF',
  isFr: boolean = false
): FormattedPriceResult {
  const rawXaf = parsePriceToXaf(price);
  const converted = convertFromXaf(rawXaf, targetCurrency);

  const formattedXaf = formatXafWithCode(rawXaf, isFr);

  if (targetCurrency === 'XAF' || rawXaf <= 0) {
    return {
      primary: formattedXaf,
      currency: 'XAF',
      rawXaf,
      convertedAmount: rawXaf,
    };
  }

  const formattedForeign =
    targetCurrency === 'EUR'
      ? `${converted.toLocaleString(isFr ? 'fr-FR' : 'en-US')} €`
      : targetCurrency === 'GBP'
      ? `£${converted.toLocaleString('en-GB')}`
      : targetCurrency === 'CAD'
      ? `CA$${converted.toLocaleString('en-CA')}`
      : `$${converted.toLocaleString('en-US')}`;

  return {
    primary: formattedForeign,
    secondary: formattedXaf,
    currency: targetCurrency,
    rawXaf,
    convertedAmount: converted,
  };
}

export const CurrencyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currency, setCurrencyState] = useState<CurrencyCode>(() => {
    try {
      const saved = localStorage.getItem('cameroon_currency');
      if (saved && saved in CURRENCY_CONFIGS) {
        return saved as CurrencyCode;
      }
    } catch {
      // fallback
    }
    return 'XAF';
  });

  const setCurrency = (code: CurrencyCode) => {
    if (code in CURRENCY_CONFIGS) {
      setCurrencyState(code);
      try {
        localStorage.setItem('cameroon_currency', code);
      } catch {
        // ignore
      }
    }
  };

  const config = CURRENCY_CONFIGS[currency];

  const formatPrice = (price?: string | number | null, isFr: boolean = false) => {
    return formatPriceResult(price, currency, isFr);
  };

  return (
    <CurrencyContext.Provider
      value={{
        currency,
        setCurrency,
        config,
        parsePriceToXaf,
        convertFromXaf: (amt, curr) => convertFromXaf(amt, curr || currency),
        formatPrice,
        formatXafWithCode,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};

export function useCurrency(): CurrencyContextType {
  const context = useContext(CurrencyContext);
  if (!context) {
    const fallbackConfig = CURRENCY_CONFIGS.XAF;
    return {
      currency: 'XAF',
      setCurrency: () => {},
      config: fallbackConfig,
      parsePriceToXaf,
      convertFromXaf: (amt) => amt,
      formatPrice: (p, isFr) => formatPriceResult(p, 'XAF', isFr),
      formatXafWithCode,
    };
  }
  return context;
}
